import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Sparkles, Zap, BookOpen, Trophy, ArrowRight, ShieldCheck } from "lucide-react";
import Nav from "../components/Nav";

const feature = (Icon, title, desc, color) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="brutal-card p-8"
  >
    <div className={`w-14 h-14 rounded-2xl border-2 border-brand-ink shadow-brutal flex items-center justify-center mb-5 ${color}`}>
      <Icon className="w-7 h-7 text-brand-navy" strokeWidth={2.5} />
    </div>
    <h3 className="font-display text-2xl font-bold text-brand-navy mb-2">{title}</h3>
    <p className="text-brand-navy/70 leading-relaxed">{desc}</p>
  </motion.div>
);

export default function Landing() {
  return (
    <div className="min-h-screen grain-bg" data-testid="landing-page">
      <Nav />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-5xl sm:text-6xl md:text-7xl font-bold text-brand-navy leading-[1.02]"
            >
              Learn a language.
              <br />
              <span className="inline-block bg-brand-yellow border-2 border-brand-ink rounded-2xl px-4 py-1 shadow-brutal my-2 -rotate-2">
                Actually stick with it.
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-8 text-lg md:text-xl text-brand-navy/80 max-w-xl leading-relaxed"
            >
              Bite-size flashcard sessions, spaced repetition that actually works, and an AI conversation
              partner that talks back — all wrapped in a game you'll want to play daily.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/courses" data-testid="hero-cta-browse" className="brutal-btn-pink text-lg px-8 py-4">
                Browse courses <ArrowRight className="w-5 h-5" strokeWidth={3} />
              </Link>
              <Link to="/login" data-testid="hero-cta-login" className="brutal-btn-white text-lg px-8 py-4">
                Sign in
              </Link>
              <Link
                to="/login?as=admin"
                data-testid="hero-cta-admin"
                className="brutal-btn-white text-sm px-5 py-3"
                title="Sign in to the admin studio"
              >
                <ShieldCheck className="w-4 h-4" strokeWidth={3} /> Admin login
              </Link>
            </motion.div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm font-bold text-brand-navy/80">
              <div className="flex items-center gap-2"><Flame className="w-5 h-5 text-brand-pink" strokeWidth={3} /> 12,000+ streaks going strong</div>
              <div className="flex items-center gap-2"><Trophy className="w-5 h-5 text-brand-yellow" strokeWidth={3} /> #1 daily habit for learners</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="brutal-card p-2 rotate-2">
                <img
                  src="https://images.unsplash.com/photo-1595986630530-969786b19b4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxoYXBweSUyMHN0dWRlbnQlMjB1c2luZyUyMHBob25lfGVufDB8fHx8MTc4NDU5ODI0MXww&ixlib=rb-4.1.0&q=85"
                  alt="Learner practicing"
                  className="w-full h-[420px] object-cover rounded-xl"
                />
              </div>
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -left-6 top-10 brutal-card-sm px-4 py-3 bg-brand-mint"
              >
                <div className="text-xs font-bold uppercase text-brand-navy/70">You said</div>
                <div className="font-display text-xl font-bold text-brand-navy">¡Hola, amigo!</div>
              </motion.div>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 3.5, repeat: Infinity }}
                className="absolute -right-4 bottom-16 brutal-card-sm px-4 py-3 bg-brand-pink text-white"
              >
                <div className="flex items-center gap-2">
                  <Flame className="w-5 h-5" strokeWidth={3} />
                  <div>
                    <div className="text-xs font-bold uppercase opacity-80">Streak</div>
                    <div className="font-display text-xl font-bold">28 days</div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -bottom-6 left-10 brutal-card-sm px-4 py-3 bg-brand-yellow"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-brand-navy" strokeWidth={3} />
                  <div className="font-display text-xl font-bold text-brand-navy">+120 XP</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <div className="max-w-2xl mb-14">
          <div className="brutal-badge bg-brand-yellow mb-4"><Zap className="w-3.5 h-3.5" strokeWidth={3} /> Built for real learning</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-navy">
            Five ways LingoFacil makes it stick.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {feature(BookOpen, "Smart flashcards", "Multiple choice + typing quiz modes keep sessions fresh so you never zone out.", "bg-brand-yellow")}
          {feature(Zap, "Spaced repetition", "SM-2 algorithm resurfaces cards right before you forget them.", "bg-brand-mint")}
          {feature(Sparkles, "AI deck generator", "Type a topic — get a beginner-friendly vocab deck in seconds.", "bg-brand-blue")}
          {feature(Flame, "Streaks & XP", "Daily habit loop that turns learning into a game worth showing up for.", "bg-brand-yellow")}
          {feature(Trophy, "Community ranks", "Climb the weekly leaderboard alongside learners around the world.", "bg-brand-mint")}
        </div>
      </section>

      <footer className="border-t-2 border-brand-ink bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm font-bold text-brand-navy/70">
          <div>© 2026 LingoFacil. Made with grit + coffee.</div>
          <div className="flex gap-6 items-center">
            <Link to="/courses">Courses</Link>
            <Link to="/leaderboard">Leaderboard</Link>
            <Link to="/login">Log in</Link>
            <Link
              to="/login?as=admin"
              data-testid="footer-admin-login"
              className="inline-flex items-center gap-1 text-brand-pink hover:underline"
            >
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={3} /> Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
